// =============================================================================
// TASK-9887 — Locates (811) page search: find a ticket the way a dispatcher
// thinks — part of a ticket #, a site / lease fragment, the customer, county or
// township, an address, the project, a job #, the crew, or a date ("good
// through next week", "oct 20", "10/20", "expired", "expiring"); typos and
// spacing tolerated.
//
// Side-effect module: attaches `window.Locates811Search` (read at CALL time by
// dispatch.js — never cross-imported, Pitfall #1). Browser-only on purpose:
// the server's find_811_tickets keeps its own matcher, and nothing here adds
// to the Cloud Functions cold-start budget. The small date / ticket-number
// helpers below are copies of the canonical rules in
// functions/src/shared/locates811.js (validDateKey, addDays, ticketNoFromFile,
// effectiveStatus, Lev) so this file stands alone and loads in any order.
//
// Pure: no DOM, no clock (todayKey is always passed in).
// Unit test: tests/task9887-locates-search.unit.cjs
// =============================================================================
(function () {
'use strict';

function l811ValidDateKey(v) {
  if (typeof v !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(v)) return '';
  var ms = Date.UTC(+v.slice(0, 4), +v.slice(5, 7) - 1, +v.slice(8, 10));
  return new Date(ms).toISOString().slice(0, 10) === v ? v : '';
}
function l811AddDays(key, n) {
  if (!l811ValidDateKey(key) || typeof n !== 'number' || !isFinite(n)) return '';
  var ms = Date.UTC(+key.slice(0, 4), +key.slice(5, 7) - 1, +key.slice(8, 10)) + Math.round(n) * 86400000;
  return new Date(ms).toISOString().slice(0, 10);
}
function l811TicketNoFromFile(fileName) {
  var m = /^\s*(\d{5,})(?!\d)/.exec(String(fileName || ''));
  return m ? m[1].slice(0, 40) : '';
}
function l811EffectiveStatus(p, todayKey) {
  if (!p || typeof p !== 'object') return 'requested';
  if (p.status === 'renewed' || (typeof p.renewedById === 'string' && p.renewedById)) return 'renewed';
  var exp = l811ValidDateKey(p.expiresOn);
  if (exp && l811ValidDateKey(todayKey) && exp < todayKey) return 'expired';
  return p.status === 'clear' ? 'clear' : 'requested';
}
function l811Lev(a, b) {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  var prev = [];
  for (var j0 = 0; j0 <= b.length; j0++) prev.push(j0);
  for (var i = 1; i <= a.length; i++) {
    var cur = [i];
    for (var j = 1; j <= b.length; j++) {
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    prev = cur;
  }
  return prev[b.length];
}

// ── TASK-9887 — Locates search, the way a dispatcher looks for a ticket ─────
// parseSearch() reads the query ONCE: date phrases ("good through next week",
// "oct 20", "10/20", "expired", "expiring") become filters; every other word
// must be found in the ticket (whole or part, typo-tolerant). matchTicket()
// scores one ticket: 0 = no match. `extra` = text the caller resolves that is
// not on the ticket doc (project names, job numbers + titles, crew names,
// customer name). Pure; no DOM, no clock (todayKey is passed in).
var L811_SEARCH_WHAT = 'ticket #, site, customer, county, township, address, project, job #, crew and dates';
var L811_SEARCH_STOP = ['the', 'a', 'an', 'at', 'for', 'of', 'on', 'in', 'to', 'and', 'by', 'is', 'ticket', 'tickets', 'locate', 'locates', 'no', 'num', 'number'];
var L811_SEARCH_DATEWORDS = ['good', 'through', 'thru', 'till', 'until', 'expires', 'expire', 'expiry', 'exp', 'dig', 'digging', 'start', 'starts', 'starting', 'date', 'dated', 'due', 'valid'];
var L811_MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
var L811_LOCATION_KEYS = ['address', 'legal', 'county', 'state', 'excavator', 'workDoneFor', 'place', 'township', 'city',
  'purpose', 'ticketType', 'relocateOf']; // TASK-9887 E2E: purpose words ("anode"), and the OLD ticket # a renewal names
// "McKenzie county" / "Ward co": the county word itself is not on the ticket
// text, so it is dropped when it rides with another word.
var L811_COUNTY_WORDS = ['county', 'cnty', 'cty', 'co'];

/**
 * TASK-9887 — township / range / section in one spelling: "T157 R90",
 * "Twp 157N Rng 90W", "T-157N R-90W", "157N90W14" -> "t157 r90 (s14)". Applied
 * to the query (replacing) and to the ticket text (added beside the original).
 */
function l811TrsNorm(s) {
  return String(s == null ? '' : s).toLowerCase()
    .replace(/\b(\d{2,3})([ns])(\d{2,3})([ew])(\d{1,2})?(?![0-9])/g, function (m, t, ns, r, ew, sec) { return ' t' + t + ' r' + r + (sec ? ' s' + (+sec) : '') + ' '; })
    .replace(/\b(?:twp|township|twnshp|t)\s*[-.]?\s*(\d{2,3})\s*[ns]?(?![a-z0-9])/g, ' t$1 ')
    .replace(/\b(?:rng|range|r)\s*[-.]?\s*(\d{2,3})\s*[ew]?(?![a-z0-9])/g, ' r$1 ')
    .replace(/\b(?:sec|sect|section)(?:\s*-?\s*qtr)?\s*[-.:]?\s*(\d{1,2})(?![0-9])/g, function (m, n) { return ' s' + (+n) + ' '; });
}
/** Only the canonical t/r/s tokens found in s ('' when none). */
function l811TrsTokens(s) {
  var out = l811TrsNorm(s).match(/\b[trs]\d{1,3}\b/g);
  return out ? out.join(' ') : '';
}

/** Lowercase, "township" -> "twp", punctuation -> space, one space. */
function l811SearchNorm(s) {
  return String(s == null ? '' : s).toLowerCase()
    .replace(/\btownship\b/g, 'twp').replace(/\btwnshp\b/g, 'twp')
    .replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, ' ').trim();
}

/** Monday-start week containing `key`: [mon, sun]. */
function l811WeekOf(key) {
  var k = l811ValidDateKey(key);
  if (!k) return null;
  var dow = new Date(Date.UTC(+k.slice(0, 4), +k.slice(5, 7) - 1, +k.slice(8, 10))).getUTCDay();
  var mon = l811AddDays(k, -((dow + 6) % 7));
  return [mon, l811AddDays(mon, 6)];
}

/** "Oct 20" / "10/20" with no year: the year that puts it nearest today. */
function l811NearestYear(month, day, todayKey) {
  var y = +String(todayKey).slice(0, 4) || new Date().getUTCFullYear();
  var best = '';
  var bestGap = Infinity;
  for (var dy = -1; dy <= 1; dy++) {
    var k = l811ValidDateKey((y + dy) + '-' + (month < 10 ? '0' : '') + month + '-' + (day < 10 ? '0' : '') + day);
    if (!k) continue;
    var gap = Math.abs(Date.parse(k) - Date.parse(todayKey));
    if (gap < bestGap) { bestGap = gap; best = k; }
  }
  return best;
}

/**
 * Read a search query. Returns { words, joined, dates: [{from,to}], field:
 * 'expires'|'dig'|'any', status: ''|'expired'|'soon', number, empty }.
 */
function l811ParseSearch(query, todayKey) {
  var raw = String(query == null ? '' : query).toLowerCase().slice(0, 200);
  var out = { words: [], joined: '', dates: [], field: 'expires', status: '', number: false, empty: true };
  var today = l811ValidDateKey(todayKey);
  var used = false;
  var addRange = function (from, to) { if (from && to) { out.dates.push({ from: from, to: to }); used = true; } };
  // Explicit dates first (they hold / and -, which normalizing would split).
  raw = raw.replace(/\b(\d{4})-(\d{1,2})-(\d{1,2})\b/g, function (m, y, mo, d) {
    var k = l811ValidDateKey(y + '-' + ('0' + mo).slice(-2) + '-' + ('0' + d).slice(-2));
    if (k) { addRange(k, k); out.field = 'any'; return ' '; }
    return m;
  });
  raw = raw.replace(/\b(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?\b/g, function (m, mo, d, y) {
    var k = '';
    if (y) k = l811ValidDateKey((y.length === 2 ? '20' + y : y) + '-' + ('0' + mo).slice(-2) + '-' + ('0' + d).slice(-2));
    else if (today) k = l811NearestYear(+mo, +d, today);
    if (k) { addRange(k, k); out.field = 'any'; return ' '; }
    return m;
  });
  raw = raw.replace(/\b(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*\.?\s+(\d{1,2})(?:st|nd|rd|th)?\b(?:,?\s*(\d{4}))?/g, function (m, mon, d, y) {
    var mo = L811_MONTHS.indexOf(mon.slice(0, 3)) + 1;
    var k = y ? l811ValidDateKey(y + '-' + ('0' + mo).slice(-2) + '-' + ('0' + d).slice(-2)) : (today ? l811NearestYear(mo, +d, today) : '');
    if (k) { addRange(k, k); out.field = 'any'; return ' '; }
    return m;
  });
  var norm = l811SearchNorm(l811TrsNorm(raw));
  if (today) {
    norm = norm.replace(/\bnext week\b/g, function () { var w = l811WeekOf(l811AddDays(today, 7)); addRange(w[0], w[1]); return ' '; });
    norm = norm.replace(/\bthis week\b/g, function () { var w = l811WeekOf(today); addRange(today, w[1]); return ' '; });
    norm = norm.replace(/\btomorrow\b/g, function () { var t = l811AddDays(today, 1); addRange(t, t); return ' '; });
    norm = norm.replace(/\btoday\b/g, function () { addRange(today, today); return ' '; });
  }
  norm = norm.replace(/\b(expired|lapsed)\b/g, function () { out.status = 'expired'; used = true; return ' '; });
  norm = norm.replace(/\b(expiring|expires soon|expire soon|soon|renew|renewal|due soon)\b/g, function () { if (out.status !== 'expired') out.status = 'soon'; used = true; return ' '; });
  var words = norm.split(' ').filter(Boolean);
  if (words.indexOf('dig') >= 0 || words.indexOf('digging') >= 0 || words.indexOf('start') >= 0 || words.indexOf('starts') >= 0 || words.indexOf('starting') >= 0) out.field = 'dig';
  else if (words.some(function (w) { return ['good', 'through', 'thru', 'expires', 'expire', 'until', 'till', 'valid'].indexOf(w) >= 0; })) out.field = 'expires';
  words = words.filter(function (w) {
    if (L811_SEARCH_STOP.indexOf(w) >= 0) return false;
    if (used && L811_SEARCH_DATEWORDS.indexOf(w) >= 0) return false;
    return true;
  });
  // "good through" with no date is not a site word either.
  if (!used) words = words.filter(function (w) { return ['good', 'through', 'thru'].indexOf(w) < 0 || words.length === 1; });
  // "McKenzie county", "ward co": the county word goes when another word stays.
  if (words.length > 1) {
    var nc = words.filter(function (w) { return L811_COUNTY_WORDS.indexOf(w) < 0; });
    if (nc.length) { out.county = nc.length < words.length; words = nc; }
  }
  out.words = words;
  out.joined = words.join('');
  out.number = words.some(function (w) { return /^\d{5,}$/.test(w); }) || /^\d{5,}$/.test(out.joined);
  out.empty = !words.length && !out.dates.length && !out.status;
  return out;
}

/** The digits a ticket number is: stored, else what its name / PDF name lead with. */
function l811TicketDigits(p) {
  var stored = String((p && p.ticketNumber) || '').replace(/\D+/g, '');
  return stored || l811TicketNoFromFile(p && p.name) || l811TicketNoFromFile(p && p.pdfName) || '';
}

/**
 * Score one ticket against a parsed query. 0 = not a match. Higher = better.
 * opts: { todayKey, soonDays (default 3), extra: string[], statusOf (optional, TASK-9925) }.
 */
function l811MatchTicket(p, parsed, opts) {
  if (!p || typeof p !== 'object' || !parsed) return 0;
  var o = opts || {};
  if (parsed.empty) return 1;
  var today = l811ValidDateKey(o.todayKey);
  var exp = l811ValidDateKey(p.expiresOn);
  var dig = l811ValidDateKey(p.digStartOn);
  if (parsed.status) {
    if (!exp || !today) return 0;
    var soonDays = typeof o.soonDays === 'number' ? o.soonDays : 3;
    // TASK-9925 — the caller's status rule when given (dispatch.js passes the
    // card's: SITE time zone, this instant), so "expired" finds what the card
    // calls expired. The date-only copy is the fallback.
    var isExpired = typeof o.statusOf === 'function' ? o.statusOf(p) === 'expired' : l811EffectiveStatus(p, today) === 'expired';
    if (parsed.status === 'expired' && !isExpired) return 0;
    if (parsed.status === 'soon' && (isExpired || exp > l811AddDays(today, soonDays))) return 0;
  }
  for (var r = 0; r < parsed.dates.length; r++) {
    var rg = parsed.dates[r];
    var inR = function (k) { return !!k && k >= rg.from && k <= rg.to; };
    var hit = parsed.field === 'dig' ? inR(dig) : (parsed.field === 'any' ? (inR(exp) || inR(dig)) : inR(exp));
    if (!hit) return 0;
  }
  var score = 1;
  if (!parsed.words.length) return score;
  var tn = l811TicketDigits(p);
  var f = (p.readFromPdf && typeof p.readFromPdf === 'object' && p.readFromPdf.fields && typeof p.readFromPdf.fields === 'object') ? p.readFromPdf.fields : {};
  var parts = [tn, p.ticketNumber, p.name, p.pdfName, p.siteName, p.pendingJobName, p.proposedProjectName, p.customerName];
  for (var i = 0; i < L811_LOCATION_KEYS.length; i++) {
    var v = f[L811_LOCATION_KEYS[i]];
    if (typeof v === 'string') parts.push(v);
    var top = p[L811_LOCATION_KEYS[i]];
    if (typeof top === 'string') parts.push(top);
  }
  // TASK-9922 r3 (COD blast-radius) — a backfilled siteLocation is searchable too.
  if (p.siteLocation && typeof p.siteLocation === 'object') {
    if (typeof p.siteLocation.state === 'string') parts.push(p.siteLocation.state);
    if (typeof p.siteLocation.county === 'string') parts.push(p.siteLocation.county);
  }
  var extra = Array.isArray(o.extra) ? o.extra : [];
  for (var e = 0; e < extra.length; e++) parts.push(extra[e]);
  var text = parts.map(function (x) { return String(x == null ? '' : x).slice(0, 400); }).join(' ');
  var hay = ' ' + l811SearchNorm(text + ' ' + l811TrsTokens(text)) + ' ';
  var squashed = hay.replace(/ /g, '');
  var tokens = hay.trim().split(' ');
  // "McKenzie county": a ticket IN that county outranks one whose customer is McKenzie Energy.
  if (parsed.county) {
    var cty = ' ' + l811SearchNorm(f.county || p.county || '') + ' ';
    if (cty.trim() && parsed.words.every(function (x) { return cty.indexOf(' ' + x + ' ') >= 0; })) score += 5;
  }
  // Spacing: "lost wood" finds "Lostwood"; "2614 7145" finds 26147145.
  if (parsed.words.length > 1 && parsed.joined.length >= 4) {
    if (tn && tn.indexOf(parsed.joined) >= 0) return score + 8 * parsed.words.length;
    if (squashed.indexOf(parsed.joined) >= 0) return score + 3 * parsed.words.length;
  }
  for (var w = 0; w < parsed.words.length; w++) {
    var q = parsed.words[w];
    if (/^\d+$/.test(q)) {
      if (tn && tn === q) { score += 10; continue; }
      if (tn && q.length >= 3 && tn.indexOf(q) === 0) { score += 7; continue; }
      if (tn && q.length >= 3 && tn.indexOf(q) >= 0) { score += 6; continue; }
      if (hay.indexOf(' ' + q) >= 0) { score += 3; continue; }
      if (q.length >= 3 && hay.indexOf(q) >= 0) { score += 2; continue; }
      return 0;
    }
    if (hay.indexOf(' ' + q) >= 0) { score += 4; continue; }
    if (hay.indexOf(q) >= 0) { score += 3; continue; }
    if (q.length >= 4 && squashed.indexOf(q) >= 0) { score += 2; continue; }
    // Typos: one slip in a 4-6 letter word, two in a longer one, against a
    // whole word or the start of a longer one ("bridgr" -> "bridger").
    // A word with digits in it is never fuzzy (t157 is not t152; 14-23H is not 14-28H).
    if (q.length >= 4 && !/\d/.test(q)) {
      var max = q.length >= 7 ? 2 : 1;
      var fuzzy = false;
      for (var t = 0; t < tokens.length && !fuzzy; t++) {
        var tok = tokens[t];
        if (!tok || /^\d+$/.test(tok) || Math.abs(tok.length - q.length) > max + 3) continue;
        if (l811Lev(q, tok) <= max || (tok.length > q.length && l811Lev(q, tok.slice(0, q.length)) <= max && q.slice(0, 2) === tok.slice(0, 2))) fuzzy = true;
      }
      if (fuzzy) { score += 1; continue; }
    }
    return 0;
  }
  return score;
}

// ── TASK-9887 slice 4 — "potential" jobs / projects ────────────────────────
// An 811 person often knows the work before dispatch does. A potential is
// tenants/{tid}/potentials/{id}: { kind 'job'|'project', name, siteName,
// customerId, customerName, permitIds[] (<= 20), notes, status
// 'open'|'wont'|'used', usedKind, usedId, directions, waypoints, created*/
// updated*/status* stamps }. Never on the board.
// Lifecycle (Chris 10/15): it SUGGESTS while any linked ticket is valid and
// for 30 days after the last one expires; then it is ARCHIVED — never deleted,
// search still finds it. 'wont' (the 811 person: "won't happen") and 'used'
// (a dispatcher pulled it into a real job/project) stop suggesting at once.
// No dated ticket: 30 days after it was made.
var L811_POT_GRACE_DAYS = 30;
var L811_POT_MATCH_MIN = 0.8;

/** A ticket id, followed through renewals to the ticket that replaced it. */
function l811CurrentTicket(id, byId) {
  var p = byId[id];
  for (var hop = 0; p && typeof p.renewedById === 'string' && p.renewedById && byId[p.renewedById] && hop < 50; hop++) p = byId[p.renewedById];
  return p || null;
}
function l811ById(permits) {
  var m = {};
  for (var i = 0; i < (permits || []).length; i++) { var p = permits[i]; if (p && p.id) m[p.id] = p; }
  return m;
}
/** The potential's tickets as they are NOW (renewed ones replaced, deleted ones dropped), no repeats. */
function l811PotentialTickets(pot, permits) {
  var byId = l811ById(permits);
  var out = [];
  var seen = {};
  var ids = pot && Array.isArray(pot.permitIds) ? pot.permitIds : [];
  for (var i = 0; i < ids.length; i++) {
    var p = l811CurrentTicket(ids[i], byId);
    if (p && !seen[p.id]) { seen[p.id] = 1; out.push(p); }
  }
  return out;
}
/** 'open' (suggesting) | 'archived' | 'wont' | 'used'. createdKey = the day it was made. */
function l811PotentialState(pot, permits, todayKey, createdKey) {
  if (!pot || typeof pot !== 'object') return 'archived';
  if (pot.status === 'wont') return 'wont';
  if (pot.status === 'used') return 'used';
  var today = l811ValidDateKey(todayKey);
  if (!today) return 'open';
  var last = '';
  var ts = l811PotentialTickets(pot, permits);
  for (var i = 0; i < ts.length; i++) {
    var e = l811ValidDateKey(ts[i].expiresOn);
    if (e && e > last) last = e;
  }
  if (!last) last = l811ValidDateKey(createdKey) || today;
  return today <= l811AddDays(last, L811_POT_GRACE_DAYS) ? 'open' : 'archived';
}
/**
 * The day a potential stopped suggesting ('' while it still does):
 * won't happen / used = the day that was marked (statusKey, else createdKey);
 * archived = 30 days after its last ticket expired.
 */
function l811PotentialEndedOn(pot, permits, todayKey, createdKey, statusKey) {
  var st = l811PotentialState(pot, permits, todayKey, createdKey);
  if (st === 'open') return '';
  if (st === 'wont' || st === 'used') return l811ValidDateKey(statusKey) || l811ValidDateKey(createdKey) || '';
  var last = '';
  var ts = l811PotentialTickets(pot, permits);
  for (var i = 0; i < ts.length; i++) {
    var e = l811ValidDateKey(ts[i].expiresOn);
    if (e && e > last) last = e;
  }
  last = last || l811ValidDateKey(createdKey);
  return last ? l811AddDays(last, L811_POT_GRACE_DAYS) : '';
}
/**
 * Company retention (settings.potentialRetentionMonths: 0 = keep forever, the
 * default; 12 | 24): true once a potential that stopped suggesting is older
 * than that, so Locates stops showing it. Nothing is deleted here.
 */
function l811PotentialPastRetention(pot, permits, todayKey, createdKey, statusKey, months) {
  var m = months === 12 || months === 24 ? months : 0;
  var today = l811ValidDateKey(todayKey);
  if (!m || !today) return false;
  var ended = l811PotentialEndedOn(pot, permits, today, createdKey, statusKey);
  if (!ended) return false;
  var y = +ended.slice(0, 4), mo = +ended.slice(5, 7) - 1 + m, d = +ended.slice(8, 10);
  var dt = new Date(Date.UTC(y, mo, 1));
  var dim = new Date(Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth() + 1, 0)).getUTCDate();
  var limit = new Date(Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth(), Math.min(d, dim))).toISOString().slice(0, 10);
  return today > limit;
}
function l811NameKey(s) { return l811SearchNorm(s).replace(/ /g, ''); }
function l811NameSimilarity(a, b) {
  var x = l811NameKey(a), y = l811NameKey(b);
  if (!x || !y) return 0;
  if (x === y) return 1;
  var sim = 1 - l811Lev(x, y) / Math.max(x.length, y.length);
  if ((x.length >= 5 && y.indexOf(x) >= 0) || (y.length >= 5 && x.indexOf(y) >= 0)) sim = Math.max(sim, 0.85);
  return sim;
}
/**
 * The open potential a dispatcher's new job / project most likely IS, or null.
 * q = { kind, customerId, name }. A potential naming a DIFFERENT customer never
 * matches; one with no customer can. Name is compared with the potential's
 * name and its site. pots carry `_createdKey` (the caller derives it).
 */
function l811PotentialMatch(pots, permits, q, todayKey) {
  var name = q && q.name;
  if (!name || l811NameKey(name).length < 3) return null;
  var best = null;
  for (var i = 0; i < (pots || []).length; i++) {
    var pot = pots[i];
    if (!pot || !pot.id || l811PotentialState(pot, permits, todayKey, pot._createdKey) !== 'open') continue;
    if (pot.customerId && q.customerId && pot.customerId !== q.customerId) continue;
    var s = Math.max(l811NameSimilarity(name, pot.name), l811NameSimilarity(name, pot.siteName));
    if (s < L811_POT_MATCH_MIN) continue;
    var score = s + (pot.customerId && pot.customerId === q.customerId ? 0.1 : 0) + (pot.kind === q.kind ? 0.05 : 0);
    if (!best || score > best.score) best = { pot: pot, score: score };
  }
  return best ? best.pot : null;
}

window.Locates811Search = Object.freeze({
  POT_GRACE_DAYS: L811_POT_GRACE_DAYS,
  potentialTickets: l811PotentialTickets,
  potentialState: l811PotentialState,
  potentialMatch: l811PotentialMatch,
  potentialEndedOn: l811PotentialEndedOn,
  potentialPastRetention: l811PotentialPastRetention,
  nameSimilarity: l811NameSimilarity,
  SEARCH_WHAT: L811_SEARCH_WHAT,
  searchNorm: l811SearchNorm,
  parseSearch: l811ParseSearch,
  matchTicket: l811MatchTicket,
  ticketDigits: l811TicketDigits,
});
})();
